/**
 * Social Media Planner Types
 * These types match the Supabase schema and provide enhanced type safety
 * for the social media planner module
 */

/**
 * Post status - represents the current state of a post in the publishing workflow
 * Includes content approval workflow states
 */
export type PostStatus = 
  | 'draft'     // Initial working state
  | 'reviewing' // In content review process
  | 'approved'  // Approved but not yet scheduled/published 
  | 'rejected'  // Failed content review
  | 'scheduled' // Approved and scheduled for future publishing
  | 'published' // Successfully published
  | 'failed'    // Failed to publish due to API errors
  | 'pending';  // In process of publishing

/**
 * Post scheduling options
 */
export type ScheduleType = 'immediate' | 'scheduled' | 'queue' | 'recurrence';

/**
 * Recurrence patterns for scheduled posts
 */
export type RecurrencePattern = 'daily' | 'weekly' | 'monthly' | 'custom';

/**
 * Frequency settings for recurring posts
 */
export interface RecurrenceSettings {
  pattern: RecurrencePattern;
  daysOfWeek?: number[]; // 0 = Sunday, 1 = Monday, etc.
  daysOfMonth?: number[];
  repeatUntil?: string; // ISO date string
  timesPerDay?: number;
  timeSlots?: string[]; // Time in 24hr format e.g. '14:30'
  timezone?: string; // IANA timezone string (e.g., 'America/New_York')
}

/**
 * Settings for automatically recycling evergreen content
 */
export interface RecyclingSettings {
  // How often to recycle this post (in days)
  interval: number;
  // Maximum number of times to recycle the post (0 = unlimited)
  max_recycles?: number;
  // Whether to automatically randomize content slightly for each recycling
  randomize_content?: boolean;
  // When to start recycling (defaults to after publishing)
  start_after?: string;
  // When to stop recycling this post
  end_after?: string;
  // What times of day are preferred for recycling
  preferred_times?: string[];
  // Whether to respect best time recommendations when recycling
  use_best_time?: boolean;
}

/**
 * Main social post interface
 * Represents a post that can be scheduled, published, and tracked
 */
export interface SocialPost {
  id?: string;
  user_id?: string;
  content: string;
  media_urls?: string[];
  platforms: SocialPlatform[]; // Improved typing from string[] to SocialPlatform[]
  scheduled_for?: string | null;
  published_at?: string | null;
  status?: PostStatus;
  // External platform identifier (e.g., LinkedIn URN) when published externally
  external_post_urn?: string | null;
  hashtags?: string[];
  created_at?: string;
  updated_at?: string;
  platform_details?: PlatformPostDetail[];
  color?: string; // Hex color code for color-coding in calendar
  recurrence_rule?: string; // iCalendar format recurrence rule
  analytics?: PostAnalytics;
  schedule_type?: ScheduleType;
  recurrence?: RecurrenceSettings;
  tags?: string[];
  category?: string;
  is_template?: boolean;
  // Recycling settings for evergreen content
  is_evergreen?: boolean;      // Flag to mark content as evergreen (can be recycled)
  recycling_settings?: RecyclingSettings; // Settings for how to recycle this post
  last_recycled_at?: string;   // When the post was last recycled
  recycle_count?: number;      // How many times this post has been recycled
  // Persistent footer settings (optional)
  append_footer?: boolean;     // If true, append footer_text to content at publish/schedule time
  footer_text?: string;        // The footer text to append (e.g., newsletter link)
}

/**
 * Platform-specific post details
 * Contains platform-specific formatting, settings, and metadata
 */
export interface PlatformPostDetail {
  id?: string;
  post_id?: string;
  platform: SocialPlatform; // Changed from string to SocialPlatform for type safety
  platform_specific_content?: string;
  image_alt?: string;
  link_preview_enabled?: boolean;
  character_count?: number;
  platform_post_id?: string; 
  engagement_metrics?: Record<string, number>; // More specific than 'any'
  created_at?: string;
  updated_at?: string;
  publish_error?: string;
  is_valid?: boolean;
  validation_errors?: ValidationError[];
  custom_link?: string;
  first_comment?: string; // For platforms that support first comment (like Instagram)
  media_arrangement?: 'carousel' | 'grid' | 'single' | 'album';
}

/**
 * Validation error type for content validation
 */
export interface ValidationError {
  code: string;
  field: string;
  message: string;
  platform?: SocialPlatform;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Content validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * Analytics data for a post across platforms or for a specific platform
 */
export interface PostAnalytics {
  id?: string;
  post_id?: string;
  platform?: SocialPlatform;
  engagement_count?: number;
  click_count?: number;
  impression_count?: number;
  share_count?: number;
  comment_count?: number;
  like_count?: number;
  save_count?: number;
  reach?: number;
  impressions?: number; 
  video_views?: number;
  video_view_duration?: number; // in seconds
  profile_views?: number;
  follows_from_post?: number;
  recorded_at?: string;
  time_period?: 'daily' | 'weekly' | 'monthly' | 'total';
  date_range?: {
    start: string;
    end: string;
  };
}

/**
 * Media file types supported by the planner
 */
export type MediaFileType = 'image' | 'video' | 'gif' | 'audio';

/**
 * Media type categorization
 */
export type MediaType = 'image' | 'video' | 'gif' | 'audio' | 'carousel' | 'document';

/**
 * Collection types for organizing multiple media items
 */
export type CollectionType = 'carousel' | 'album' | 'gallery' | 'audio' | 'document';

/**
 * Media item for the media library
 */
export interface MediaItem {
  id?: string;
  user_id?: string;
  file_name: string;
  file_type: MediaFileType; // Specific format (image/video/gif/audio)
  media_type: MediaType;    // Higher level categorization
  file_url: string;
  file_size?: number;
  dimensions?: {
    width: number;
    height: number;
  };
  width?: number;
  height?: number;
  duration?: number;       // For videos/audio - duration in seconds
  thumbnail_url?: string;  // Thumbnail image for videos
  alt_text?: string;       // Accessibility description
  caption?: string;        // Optional caption to display with media
  tags?: string[];         // Tags for organizing and searching media
  folder_id?: string;      // Reference to a MediaFolder
  collection_id?: string;  // Reference to a MediaCollection for carousels
  position?: number;       // Position in a collection (for carousels)
  metadata?: Record<string, any>; // Platform-specific metadata
  usage_count?: number;    // How many times this media has been used
  created_at?: string;
  updated_at?: string;
  last_used_at?: string;
  is_favorite?: boolean;   // User-marked favorite status
}

/**
 * Media folder for organization
 */
export interface MediaFolder {
  id?: string;
  user_id?: string;
  name: string;
  parent_folder_id?: string;
  description?: string;
  color?: string;         // Optional color for visual organization
  item_count?: number;    // Number of media items in this folder
  created_at?: string;
  updated_at?: string;
}

/**
 * Media collection for carousel/multi-image posts
 */
export interface MediaCollection {
  id?: string;
  user_id?: string;
  name: string;
  description?: string;
  collection_type: CollectionType;
  platforms?: SocialPlatform[];
  items?: MediaItem[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Collection item for ordering media in collections
 */
export interface MediaCollectionItem {
  id?: string;
  collection_id: string;
  media_id: string;
  position: number;
  caption?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Template for creating posts quickly
 */
export interface PostTemplate {
  id?: string;
  user_id?: string;
  template_name: string;
  template_content: string;
  platforms: SocialPlatform[];
  hashtags?: string[];
  tags?: string[];
  category?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Calendar event view model
 */
export interface CalendarEvent {
  id: string;
  title: string;
  start: string; // ISO date
  end?: string; // ISO date
  allDay?: boolean;
  color?: string;
  platforms: SocialPlatform[];
  status: PostStatus;
  postId: string;
}

/**
 * Time slot for scheduling
 */
export interface TimeSlot {
  id?: string;
  user_id?: string;
  day_of_week: number; // 0 = Sunday, 1 = Monday, etc.
  time: string; // 24hr format e.g. '14:30'
  platform: SocialPlatform;
  is_optimal?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Queue settings for each platform
 */
export interface QueueSettings {
  platform: SocialPlatform;
  enabled: boolean;
  posts_per_day?: number;
  time_slots?: TimeSlot[];
  minimum_gap_hours?: number;
}

/**
 * Content category for organizing posts
 */
export interface ContentCategory {
  id?: string;
  user_id?: string;
  name: string;
  color?: string;
  post_count?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * Campaign for organizing related posts with goals and metrics
 * Groups posts together for coordinated campaigns with shared objectives
 */
export interface Campaign {
  id?: string;
  user_id?: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  posts: string[]; // Array of post IDs
  goal?: string;
  metrics?: {
    impressions?: number;
    engagement?: number;
    clicks?: number;
    conversions?: number;
    custom?: Record<string, number>;
  };
  color?: string;
  status?: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

/**
 * Hashtag Collection for organizing and reusing hashtag sets
 * Allows users to save commonly used hashtag groups for different content types
 */
export interface HashtagCollection {
  id?: string;
  user_id?: string;
  name: string;
  description?: string;
  hashtags: string[];
  category?: string;
  platform?: SocialPlatform; // Optional platform-specific hashtag set
  usage_count?: number;
  is_favorite?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Supported social media platforms
 * This list will grow as more integrations are added
 */
export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin' | 'pinterest' | 'tiktok' | 'youtube';

/**
 * Platform-specific content requirements and limitations
 */
export interface PlatformRequirements {
  maxCharCount: number;
  maxImages: number;
  maxVideos: number;
  supportedFileTypes: string[];
  maxHashtags?: number;
  characterCountWithLinks: number;
}

/**
 * Platform-specific content requirements by platform
 */
export const PLATFORM_REQUIREMENTS: Record<SocialPlatform, PlatformRequirements> = {
  facebook: {
    maxCharCount: 63206,
    maxImages: 10,
    maxVideos: 1,
    supportedFileTypes: ['jpg', 'png', 'gif', 'mp4'],
    characterCountWithLinks: 63206
  },
  instagram: {
    maxCharCount: 2200,
    maxImages: 10, // For carousel posts
    maxVideos: 1,
    supportedFileTypes: ['jpg', 'png', 'mp4'],
    maxHashtags: 30,
    characterCountWithLinks: 2200
  },
  linkedin: {
    maxCharCount: 3000,
    maxImages: 9,
    maxVideos: 1,
    supportedFileTypes: ['jpg', 'png', 'gif', 'mp4'],
    characterCountWithLinks: 3000
  },
  pinterest: {
    maxCharCount: 500,
    maxImages: 1,
    maxVideos: 1,
    supportedFileTypes: ['jpg', 'png', 'gif', 'mp4'],
    characterCountWithLinks: 500
  },
  tiktok: {
    maxCharCount: 150,
    maxImages: 0,
    maxVideos: 1,
    supportedFileTypes: ['mp4'],
    maxHashtags: 30,
    characterCountWithLinks: 150
  },
  youtube: {
    maxCharCount: 5000,
    maxImages: 1, // Thumbnail
    maxVideos: 1,
    supportedFileTypes: ['mp4', 'mov', 'avi', 'wmv'],
    characterCountWithLinks: 5000
  }
};
